import { useEffect, useRef } from "react";

/** Cross-border opportunity flow — used on Markets page */
export default function CrossBorderFlow({ height = 460 }) {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current; if (!c) return;
    const ctx = c.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    const resize = () => {
      c.width = c.clientWidth * dpr; c.height = c.clientHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize(); window.addEventListener("resize", resize);

    const ORIGINS = [
      { y: 0.12, l: "TÜRKIYE", code: "TR" },
      { y: 0.30, l: "UNITED KINGDOM", code: "UK" },
      { y: 0.48, l: "UNITED STATES", code: "USA" },
      { y: 0.66, l: "EUROPEAN UNION", code: "EU" },
      { y: 0.84, l: "REGIONAL MARKETS", code: "WB6" },
    ];
    const DEST = [
      { y: 0.40, l: "KOSOVO", sub: "ACTIVE", code: "XK", main: true },
      { y: 0.62, l: "FUTURE MARKETS", sub: "ROADMAP", code: "—", future: true },
    ];

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const N = reduce ? 100 : 240;

    const spawn = () => {
      const o = ORIGINS[Math.floor(Math.random() * ORIGINS.length)];
      const d = Math.random() < 0.85 ? DEST[0] : DEST[1];
      return {
        ox: 0.14, oy: o.y, gx: 0.5, gy: 0.5, dx: 0.88, dy: d.y, future: d.future,
        t: Math.random(), speed: 0.0025 + Math.random() * 0.0035,
        size: 0.8 + Math.random() * 1.2,
      };
    };
    const particles = Array.from({ length: N }, spawn);
    let raf, time = 0;

    const draw = () => {
      const w = c.clientWidth, h = c.clientHeight;
      ctx.clearRect(0, 0, w, h);
      const cx = 0.5 * w, cy = 0.5 * h;

      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(Math.PI / 4);
      ctx.strokeStyle = "rgba(157,180,255,0.35)"; ctx.lineWidth = 1;
      ctx.strokeRect(-50, -50, 100, 100);
      ctx.strokeStyle = "rgba(157,180,255,0.12)";
      ctx.strokeRect(-72, -72, 144, 144);
      ctx.restore();

      ctx.fillStyle = "rgba(232,236,241,0.85)";
      ctx.font = "600 10px 'JetBrains Mono', monospace";
      ctx.textAlign = "center";
      ctx.fillText("FOREALCO", cx, cy - 80);
      ctx.fillText("STRUCTURE · COORDINATE", cx, cy + 88);
      ctx.textAlign = "start";

      ORIGINS.forEach((o) => {
        const ox = 0.14 * w, oy = o.y * h;
        ctx.fillStyle = "rgba(157,180,255,0.6)";
        ctx.beginPath(); ctx.arc(ox, oy, 4, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = "rgba(232,236,241,0.85)";
        ctx.font = "500 11px 'Inter', system-ui";
        ctx.textAlign = "right";
        ctx.fillText(o.l, ox - 12, oy + 4);
        ctx.fillStyle = "rgba(157,180,255,0.6)";
        ctx.font = "600 9px 'JetBrains Mono', monospace";
        ctx.fillText(o.code, ox - 12, oy - 8);
        ctx.textAlign = "start";
      });

      DEST.forEach((d) => {
        const dx = 0.88 * w, dy = d.y * h;
        ctx.fillStyle = d.main ? "#E8ECF1" : "rgba(232,236,241,0.4)";
        ctx.beginPath(); ctx.arc(dx, dy, d.main ? 6 : 4, 0, Math.PI * 2); ctx.fill();
        if (d.main) {
          ctx.fillStyle = "rgba(157,180,255,0.18)";
          ctx.beginPath(); ctx.arc(dx, dy, 16 + Math.sin(time / 25) * 3, 0, Math.PI * 2); ctx.fill();
        }
        // right-aligned labels so they stay inside canvas
        ctx.textAlign = "left";
        ctx.fillStyle = d.main ? "rgba(232,236,241,0.95)" : "rgba(232,236,241,0.55)";
        ctx.font = "600 11px 'Inter', system-ui";
        ctx.fillText(d.l, dx + 12, dy + 4);
        ctx.fillStyle = d.main ? "#9DB4FF" : "rgba(232,236,241,0.4)";
        ctx.font = "600 9px 'JetBrains Mono', monospace";
        ctx.fillText(d.code + " · " + d.sub, dx + 12, dy - 8);
        ctx.textAlign = "start";
      });

      particles.forEach((p, i) => {
        p.t += p.speed;
        if (p.t > 1) { particles[i] = spawn(); return; }
        const ox = p.ox * w, oy = p.oy * h, gx = p.gx * w, gy = p.gy * h, dx = p.dx * w, dy = p.dy * h;
        let x, y;
        if (p.t < 0.5) {
          const tt = p.t / 0.5, mx = (ox + gx) / 2, my = (oy + gy) / 2 - 30;
          x = (1 - tt) ** 2 * ox + 2 * (1 - tt) * tt * mx + tt ** 2 * gx;
          y = (1 - tt) ** 2 * oy + 2 * (1 - tt) * tt * my + tt ** 2 * gy;
        } else {
          const tt = (p.t - 0.5) / 0.5, mx = (gx + dx) / 2, my = (gy + dy) / 2 - 30;
          x = (1 - tt) ** 2 * gx + 2 * (1 - tt) * tt * mx + tt ** 2 * dx;
          y = (1 - tt) ** 2 * gy + 2 * (1 - tt) * tt * my + tt ** 2 * dy;
        }
        const alpha = p.future ? 0.28 : 0.55;
        ctx.fillStyle = p.future
          ? `rgba(232,236,241,${alpha})`
          : `rgba(157,180,255,${alpha})`;
        ctx.beginPath(); ctx.arc(x, y, p.size, 0, Math.PI * 2); ctx.fill();
      });

      time += 1;
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);
  return (
    <canvas
      ref={ref}
      data-testid="cross-border-flow"
      className="w-full block"
      style={{ width: "100%", height, background: "rgba(255,255,255,0.015)", border: "1px solid rgba(255,255,255,0.06)" }}
    />
  );
}
